package com.evoapps.evoshift.ui.billing

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.TestTags
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.viewmodels.OverPlanState
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.over_plan_body
import evoshift.composeapp.generated.resources.over_plan_body_no_bigger
import evoshift.composeapp.generated.resources.over_plan_renewal
import evoshift.composeapp.generated.resources.over_plan_title
import evoshift.composeapp.generated.resources.over_plan_upgrade
import evoshift.composeapp.generated.resources.paywall_larger_team
import evoshift.composeapp.generated.resources.paywall_not_now
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/**
 * SH-220: the owner's team grew past the plan. Nobody was blocked; the banner proposes the next
 * tier ("From the next period: the plan for up to 25 people") with a one-tap [onUpgrade] through
 * the platform's own change, says honestly that the store changes nothing without the owner's
 * consent, and names the renewal date when [remindsRenewal]. [onDismiss] (null in the last days
 * before the renewal) puts it off until the app starts again. A team bigger than every tier is
 * asked to write to us instead.
 */
@Composable
fun OverPlanBanner(
    state: OverPlanState,
    remindsRenewal: Boolean,
    onUpgrade: () -> Unit,
    onDismiss: (() -> Unit)?,
    modifier: Modifier = Modifier
) {
    val team = state.seatsInUse ?: return
    val seats = state.currentSeats ?: return
    val next = state.nextSeats
    // The offers are still loading: the next tier is not known yet, so say nothing for a moment.
    if (next == null && state.offers.isEmpty()) return
    val uriHandler = LocalUriHandler.current
    Column(
        modifier = modifier
            .fillMaxWidth()
            .background(DeepNavy)
            .testTag(TestTags.OVER_PLAN_BANNER)
            .padding(horizontal = 16.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Icon(Icons.Default.Groups, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                if (next != null) {
                    Text(
                        stringResource(Res.string.over_plan_title, upToPeopleLabel(next)),
                        color = LightGold,
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
                Text(
                    stringResource(
                        if (next != null) Res.string.over_plan_body else Res.string.over_plan_body_no_bigger,
                        peopleCountLabel(team),
                        upToPeopleLabel(seats)
                    ),
                    color = Color.White.copy(alpha = 0.9f),
                    style = MaterialTheme.typography.bodySmall
                )
                val renewsAt = state.renewsAt
                if (remindsRenewal && renewsAt != null) {
                    Text(
                        stringResource(Res.string.over_plan_renewal, dayAndMonth(renewsAt.toLocalDateTime(TimeZone.currentSystemDefault()).date)),
                        color = Color.White,
                        style = MaterialTheme.typography.bodySmall,
                        fontWeight = FontWeight.SemiBold
                    )
                }
                (state.error ?: state.message)?.let {
                    Text(it.asString(), color = if (state.error != null) Color(0xFFFFB4AB) else Color.White, style = MaterialTheme.typography.bodySmall)
                }
            }
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
            if (onDismiss != null) {
                TextButton(onClick = onDismiss, modifier = Modifier.testTag(TestTags.OVER_PLAN_LATER)) {
                    Text(stringResource(Res.string.paywall_not_now), color = Color.White)
                }
            }
            if (next != null) {
                TextButton(onClick = onUpgrade, enabled = !state.isPurchasing, modifier = Modifier.testTag(TestTags.OVER_PLAN_UPGRADE)) {
                    if (state.isPurchasing) CircularProgressIndicator(Modifier.size(16.dp), color = LightGold, strokeWidth = 2.dp)
                    else Text(stringResource(Res.string.over_plan_upgrade, upToPeopleLabel(next)), color = LightGold, fontWeight = FontWeight.Bold)
                }
            } else {
                TextButton(
                    onClick = { runCatching { uriHandler.openUri("mailto:${Plans.SALES_CONTACT_EMAIL}") } },
                    modifier = Modifier.testTag(TestTags.PAYWALL_CONTACT)
                ) {
                    Text(stringResource(Res.string.paywall_larger_team), color = LightGold, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}
